import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import ActivityCard from "./ActivityCard";
import { ActivityResponse, Activity } from "@/types/activity";
import ActivityFilters from "./ActivityFilters";
import { useActivitiesSearch } from "./useActivitiesSearch";
import { MediaService } from "@/lib/hey/services/media-service";
import Hey from "@/lib/hey-utils";

interface ActivitiesDialogProps {
  recommendedActivities: ActivityResponse;
  destinationName: string;
  tripId: string;
  type?: "activity" | "food";
}

export function ActivitiesDialog({
  recommendedActivities,
  destinationName,
  tripId,
  type = "activity"
}: ActivitiesDialogProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>();
  const [searchResults, setSearchResults] = useState<ActivityResponse | null>(null);
  const [activeTab, setActiveTab] = useState("recommended");
  const [loading, setLoading] = useState(false);

  const handleSearch = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!searchQuery.trim()) return;

    setLoading(true); // Start loading
    try {
      const filters = { interests: searchQuery, nbResults: 20, minPrice: 15 };
      const activities = await Hey.HeyUtils.getInstance().getActivities(
        tripId,
        filters,
        type === "food" ? "discover-restaurants" : "discover"
      );
      setSearchResults(activities);
    } catch (error) {
      console.error("Error fetching activities:", error);
    } finally {
      setLoading(false); // Stop loading
    }
  };

  // Extract unique categories from activities
  const categories = recommendedActivities?.results
    ? [...new Set(recommendedActivities.results.flatMap(activity => activity.categories || []))]
    : [];

  // Determine activities to display based on the active tab
  const availableActivities =
    activeTab === "search" ? searchResults?.results : recommendedActivities?.results;

  const displayActivities = availableActivities
    ? selectedCategory
      ? availableActivities.filter(activity => activity.categories?.includes(selectedCategory))
      : availableActivities
    : [];

  const emptyState = (
    <div className="text-center py-10">
      <p className="text-muted-foreground mb-4">
        {type === "food"
          ? t("activities.noFoodFound", "No restaurants found for this destination and filters.")
          : t("activities.noActivitiesFound", "No activities found for this destination and filters.")}
      </p>
    </div>
  );

  // Handle category selection
  const handleCategorySelect = (category: string | undefined) => {
    setSelectedCategory(category);
  };

  return (
    <div className="w-full">
      <Tabs defaultValue="recommended" onValueChange={setActiveTab}>
        <div className="border-b  top-0 bg-background/90 backdrop-blur-sm z-10">
          <div className="flex gap-4 items-center p-2">
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="recommended">
                {type === "food" ? t("activities.recommended", "Recommended") : t("activities.featured", "Featured")}
              </TabsTrigger>
              <TabsTrigger value="search">{t("activities.search", "Search")}</TabsTrigger>
            </TabsList>
          </div>
        </div>

        <TabsContent value="search" className="px-1 py-6">
          <form onSubmit={handleSearch} className="relative mb-6">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              placeholder={
                type === "food"
                  ? t("activities.searchRestaurants", "Search restaurants...")
                  : t("activities.searchActivities", "Search activities...")
              }
              className="pl-10"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            <button
              type="submit"
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-travel-500 font-semibold"
              disabled={loading} // Disable button while loading
            >
              {loading ? t("activities.loading", "Loading...") : t("activities.search", "Search")}
            </button>
          </form>
          {loading && (
            <div className="flex justify-center py-4">
              <div className="w-8 h-8 border-4 border-travel-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </TabsContent>

         {availableActivities && availableActivities.length > 0 && !loading ? (
            <div className="space-y-6">
              {categories.length > 0 && (
                <ActivityFilters
                  categories={categories}
                  selectedCategory={selectedCategory}
                  onSelectCategory={handleCategorySelect}
                />
              )}
              <div className="space-y-4">
                {displayActivities.map((activity: Activity, index: number) => (
                  <ActivityCard
                    key={`${activity.name}-${index}`}
                    title={activity.name}
                    description={activity.description || ""}
                    imageUrl={activity.thumbnailUrl}
                    imageUrls={activity.imageUrls || []}
                    price={`${activity.minPrice ? activity.minPrice.toString() : ""}`}
                    currency={activity.currency || ""}
                    bookingUrl={activity.url || ""}
                    rating={activity.ratingAverage}
                    numberOfRatings={activity.numberOfRatings}
                    type={type}
                  />
                ))}
              </div>
            </div>
          ) : (
            emptyState
          )}
      </Tabs>
    </div>
  );
}
